import { MediaMatcher } from '@angular/cdk/layout';
import { ComponentType } from '@angular/cdk/portal';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Exception, ShipmentInfo } from '@cargos/sprintpay-models';
import { CustomMasks, CustomValidators } from '@cargos/sprintpay-utils';
import { Observable, Subject, map, of, switchMap, take, takeUntil } from 'rxjs';
import { DynamicDialogService } from 'src/app/shared/components/dynamic-dialog/services/dynamic-dialog.service';
import { FeatureRestrictionsService } from 'src/app/shared/services/features/feature-restrictions.service';
import { DestinationNeededResponseType } from 'src/app/shared/services/features/models/destinations';
import { InvalidAWBList } from 'src/app/shared/utils/invalid-awb-list-const';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { PaymentStatusType } from '../../../models/payment-status';
import { PaymentCartTypeType } from '../../../models/payment-type';
import { SearchType } from '../../../models/search-type';
import { AwbLookUpService, ConfigService, SummaryService } from '../../../services';
import { PaymentCartTypeHandlerService } from '../../../services/payment-cart-type-handler.service';
import { AutopayCancelComponent } from '../../awb-subscription/auto-pay-cancel/auto-pay-cancel.component';
import { GuestSubscriptionComponent } from '../../awb-subscription/guest-subscription/guest-subscription.component';
import { AwbSubscriptionService } from '../../awb-subscription/services/awb-subscription.service';
import { SubscriptionComponent } from '../../awb-subscription/subscription/subscription.component';
import { FacilityLocationsFormGroupType } from '../../facility-locations/models/location-form';
import { AWBSearchFormErrorJSON, AWBSearchFormGroupType } from './models/awb-search-form';
import { prefixesWithOutSubscription } from './models/const';
import { formAWBSearchErrors } from './utils/form-errors';

@Component({
    selector: 'app-sprintpay-awb-search',
    templateUrl: './awb-search.component.html',
    styleUrls: ['./awb-search.component.scss'],
    standalone: false,
})
export class AwbSearchComponent implements OnInit, OnDestroy {
    private dialogRef: MatDialogRef<SubscriptionComponent | GuestSubscriptionComponent>;
    private unsubscribe$: Subject<void>;
    private previousAWBCompletedValue = false;
    private isAWBCompleted = false;
    private mobileQueryListener: () => void;
    public mobileQuery: MediaQueryList;
    public isCompleted = false;
    public isLoading = false;
    public searchForm: AWBSearchFormGroupType;
    public maskAwb = CustomMasks.iMaskAwb;
    public formError: AWBSearchFormErrorJSON;
    public showStation = false;
    public showSearchButtonOutside: boolean;
    public searchAwbLabel: string;

    @Input() disabled = false;
    @Output() eventOnEnter = new EventEmitter<AWBSearchFormGroupType>();
    @Output() eventOnChangeLocation = new EventEmitter<FacilityLocationsFormGroupType>();
    @Output() eventOnSearching = new EventEmitter<boolean>();

    constructor(
        private fb: NonNullableFormBuilder,
        private configService: ConfigService,
        private awbLookUpService: AwbLookUpService,
        private summaryService: SummaryService,
        private router: Router,
        private media: MediaMatcher,
        private changeDetectorRef: ChangeDetectorRef,
        private matDialog: MatDialog,
        private paymentCartTypeHandlerService: PaymentCartTypeHandlerService,
        private awbSubscriptionService: AwbSubscriptionService,
        private dynamicDialogService: DynamicDialogService,
        private featureRestrictionsService: FeatureRestrictionsService
    ) {
        this.unsubscribe$ = new Subject<void>();

        this.mobileQuery = this.media.matchMedia('(max-width: 767px)');
        this.mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    }

    get mawb(): AbstractControl<string> | null {
        return this.searchForm.get('mawb');
    }

    ngOnInit(): void {
        this.createForm();
        this.setFormError();
        this.subscribeFormChanges();
        this.subscribeMobileSize();
        this.getConfig();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        this.mobileQuery.removeEventListener('change', this.mobileQueryListener);
    }

    submit(form: AWBSearchFormGroupType): void {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.eventOnSearching.emit(true);
        this.searchAWB(form)
            .pipe(
                take(1),
                switchMap((payment: ShipmentInfo | null) => {
                    if (!payment) {
                        this.isLoading = false;
                        return of(null);
                    }
                    return this.isDestinationNeeded(payment).pipe(
                        map((isDestinationNeededResponse) => {
                            return { payment, ...isDestinationNeededResponse };
                        })
                    );
                })
            )
            .subscribe({
                next: (
                    response: {
                        payment: ShipmentInfo;
                        isDestinationNeeded: boolean;
                        destination?: string;
                    } | null
                ) => {
                    if (response?.payment) {
                        const { payment, isDestinationNeeded, destination } = response;
                        this.isLoading = false;
                        this.summaryService.newCurrentPaymentStatusEvent(PaymentStatusType.PAYMENT_INCOMPLETE);
                        this.summaryService.setAndSaveSearchTypeInStorage(SearchType.awb);
                        this.eventOnSearching.emit(false);

                        if (isDestinationNeeded) {
                            this.showStation = true;
                        }
                        if (payment.isCancelAutoPayEnable === false) {
                            this.displaySubscriptionModal(
                                form.value?.mawb!,
                                this.awbSubscriptionService.isSubscriptionAvailable(),
                                'subscription'
                            );
                            return;
                        }
                        if (payment.isAutoPaySubscriptionExist) {
                            this.displayCancelAutoPay(this.mawb!.value);
                            return;
                        }

                        this.awbSubscriptionService.setIsArrivalSubscriptionExist(payment.isArrivalSubscriptionExist!);

                        if (payment.isError) {
                            if (form.value?.mawb) {
                                this.displaySubscriptionModal(form.value.mawb, false);
                            }
                            return;
                        }
                        if (this.awbSubscriptionService.isSubscriptionAvailable()) {
                            if (this.mawb?.value) {
                                this.displaySubscriptionModal(this.mawb.value, true);
                            }
                            return;
                        }
                        this.awbSubscriptionService.setIsEnabledSubscriptionWithoutRCForNFD(false);

                        if (payment && !isDestinationNeeded) {
                            this.redirectTo(destination || payment.shipmentDetails?.destination || '');
                        }
                    }
                },
                error: (error: Exception) => {
                    this.eventOnSearching.emit(false);
                    this.isLoading = false;

                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: error.type,
                    });
                },
            });
    }

    isNeccesarySubscription(payment: ShipmentInfo): boolean {
        if (!this.configService.isAllowedAWBSubscription()) {
            return false;
        }

        if (!payment?.charges?.length) {
            return true;
        }

        return !(
            this.awbLookUpService.isAWBReceivedFromFlight() ||
            this.awbLookUpService.isAWBAgentNotifiedOfArrival() ||
            this.awbLookUpService.isAWBReceivedFromShipper()
        );
    }

    searchAWB(form: AWBSearchFormGroupType): Observable<ShipmentInfo | null> {
        if (this.mawb?.hasError('invalid') && this.configService.isAllowedSearchInvalidAWB()) {
            return this.searchMAWB(form);
        }

        if (this.mawb?.hasError('invalid') && InvalidAWBList.includes(this.mawb.value)) {
            return this.searchMAWB(form);
        }

        if (form.valid) {
            return this.searchMAWB(form);
        }

        return of(null);
    }

    searchMAWB(form: AWBSearchFormGroupType): Observable<ShipmentInfo | null> {
        this.emitEventForm(form);

        return this.awbLookUpService.searchAWB(form.value?.mawb || '');
    }

    emitEventForm(form: AWBSearchFormGroupType): void {
        this.eventOnEnter.emit(form);

        if (this.isAWBCompleted) {
            this.isCompleted = true;
        }
    }

    setLocationInStorage(location: string | null): void {
        this.summaryService.setAndSaveLocationInStorage(location);
    }

    redirectTo(location: string): void {
        if (!location) {
            return;
        }

        this.setLocationInStorage(location);

        this.awbLookUpService
            .awbContainsHAWBs$()
            .pipe(take(1))
            .subscribe((awbContainsHAWBs) => {
                this.paymentCartTypeHandlerService.setPaymentCartType(
                    !awbContainsHAWBs ? PaymentCartTypeType.MAWB : PaymentCartTypeType.NONE
                );

                const mawb = this.searchForm.value.mawb;

                this.router.navigate([`/search-awb/details`], {
                    queryParams: {
                        mawb,
                        location,
                    },
                });
            });
    }

    onAWBStatusChange(): void {
        this.showStation = false;
    }

    subscribeFormChanges(): void {
        this.mawb?.valueChanges
            .pipe(
                map((values) => {
                    const lengthAWB = values?.replace(/_/g, '').length;

                    return lengthAWB === 12;
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((isAWBCompleted) => {
                if (!isAWBCompleted) {
                    this.isCompleted = false;
                }

                this.isAWBCompleted = isAWBCompleted;

                if (this.previousAWBCompletedValue !== this.isAWBCompleted) {
                    this.previousAWBCompletedValue = this.isAWBCompleted;
                    this.onAWBStatusChange();
                }
            });
    }

    createForm(): void {
        this.searchForm = this.fb.group({
            mawb: ['', [Validators.required], [CustomValidators.awbValidator()]],
        });
    }

    setFormError(): void {
        this.formError = formAWBSearchErrors;
    }

    cleanInput(): void {
        this.searchForm.reset();
        this.mawb?.setErrors(null);
    }

    isDestinationNeeded(payment: ShipmentInfo): Observable<DestinationNeededResponseType> {
        return this.featureRestrictionsService.isDestinationNeeded(payment).pipe(
            switchMap((isDestinationNeededResponse) => {
                if (!isDestinationNeededResponse.isDestinationNeeded) {
                    return of(isDestinationNeededResponse);
                }

                const allowedDestinations = this.configService.getAllowedDestinations();

                if (allowedDestinations.length === 1) {
                    this.setLocationInStorage(allowedDestinations[0]);
                    return of({
                        isDestinationNeeded: false,
                        destination: allowedDestinations[0],
                    });
                }

                return of(isDestinationNeededResponse);
            })
        );
    }

    onSelectShipmentLocation(form: FacilityLocationsFormGroupType): void {
        this.eventOnChangeLocation.emit(form);
    }

    /**
     * TODO: Remove this logic, it is temporal.
     * If it is not temporal, please move this logic to the config file
     * */
    public prefixesWithOutSubscription = prefixesWithOutSubscription;

    validateSubscriptionAvailable(): boolean {
        return this.prefixesWithOutSubscription.some((prefix) => {
            return this.mawb?.value?.includes(prefix);
        });
    }
    /** End of logic to remove */

    displaySubscriptionModal(awb: string, isEnabledSubscription: boolean, type?: string): void {
        this.awbSubscriptionService.setIsEnabledSubscriptionWithoutRCForNFD(isEnabledSubscription);
        this.awbSubscriptionService.selectAutoPayFirst(false);
        let subscription: ComponentType<SubscriptionComponent | GuestSubscriptionComponent>;
        if (type === 'subscription') {
            subscription = GuestSubscriptionComponent;
        } else {
            subscription = SubscriptionComponent;
        }
        this.awbSubscriptionService.setAwbForSubscription(awb);
        this.dialogRef = this.matDialog.open(subscription, {
            width: this.mobileQuery.matches ? 'auto' : '550px',
            maxWidth: '-webkit-fill-available',
            maxHeight: this.mobileQuery.matches ? 'auto' : '80vh',
            minHeight: '400px',
            data: { awb },
        });
        this.afterSubscriptionModalClosed();
    }

    displayCancelAutoPay(awb: string): void {
        Swal.fire({
            title: 'Good news!',
            icon: 'info',
            html: 'This <strong>MAWB has already been opted in for AutoPay.</strong> You may cancel this AutoPay, or start a new search.',
            allowOutsideClick: false,
            showCancelButton: true,
            cancelButtonText: 'CANCEL AUTOPAY',
            showCloseButton: true,
            showConfirmButton: true,
            confirmButtonText: 'CONTINUE',
            reverseButtons: true,
            customClass: {
                cancelButton:
                    'mdc-button mat-mdc-button-base h-auto mdc-button--outlined mat-mdc-outlined-button mat-primary appMatStrokedButtonBorder',
                confirmButton:
                    'mdc-button mat-mdc-button-base my-4 mdc-button--unelevated mat-mdc-unelevated-button mat-primary',
            },
        }).then((result: SweetAlertResult) => {
            Swal.close();
            this.cleanInput();
            if (result.isConfirmed) {
                const location = this.summaryService.getLocation();
                this.router.navigate([`/search-awb/details`], {
                    queryParams: {
                        mawb: this.mawb,
                        location,
                    },
                });
            }
            if (result.dismiss === Swal.DismissReason.cancel) {
                this.dynamicDialogService.showDialog<AutopayCancelComponent, { awb: string }>({
                    dynamicComponent$: AutopayCancelComponent,
                    config: {
                        width: '550px',
                        data: { awb },
                    },
                });
            }
            if (result.dismiss === Swal.DismissReason.close) {
                Swal.close();
            }
        });
    }

    afterSubscriptionModalClosed(): void {
        this.dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(() => {
                this.cleanInput();
            });
    }

    subscribeMobileSize(): void {
        this.mobileQuery.addEventListener('change', this.mobileQueryListener);
    }

    getConfig(): void {
        this.showSearchButtonOutside = this.configService.isEnableSearchButtonOutside();
        this.searchAwbLabel = this.configService.getSearchAWBLabel();
    }
}
